import { useEffect, useState, useMemo, useRef } from "react"
import { produce } from "immer"
import { isEqual, omit } from "lodash"
import { makeFindAndGetBaseMetadata } from "helpers/base"
import { ERROR, OK } from "helpers/data-statuses"
import type { SelectedInstrument, SelectedRulesInstrument } from "helpers/types/instruments/selected"
import type { Preferences, PreferencesValidationOptions } from "hooks/use-preferences/types"
import { type ActiveInstruments, useActiveInstruments, INSTRUMENT_TYPES } from "resources/instruments"
import { useGetUrlInstrumentParams, type UrlInstrument } from "../use-get-instrument-url-params"

const isRulesInstrument = (instrument: SelectedInstrument): instrument is SelectedRulesInstrument => instrument.type === "RULES"

const isComparisonInstrumentInvalid = (instrument: SelectedInstrument, validate: (i: SelectedInstrument) => unknown): boolean => {
  if (isRulesInstrument(instrument) && instrument.rulesConfiguration?.source?.positionSet) {
    return !validate(instrument.rulesConfiguration.source.positionSet)
  }
  return !validate(instrument)
}

function validatePreferences(
  preferences: Preferences,
  activeInstruments: ActiveInstruments | null,
  validationOverrides: { urlInstrument?: UrlInstrument } = {},
) {
  if (!activeInstruments) {
    return {
      isCorrected: false,
      preferences,
    }
  }

  const instrumentValidator = makeFindAndGetBaseMetadata(null, activeInstruments)
  let isCorrected = false

  const { urlInstrument } = validationOverrides || {}
  const { urlInstrumentId, urlInstrumentType } = urlInstrument || {}

  // We only want to apply the override to portfolios for the time being
  const overrideInstrument = urlInstrumentType === "portfolio" && urlInstrumentId
    ? activeInstruments.portfolios?.find((item) => item.alias === urlInstrumentId) ?? null
    : null

  const validatedPreferences = produce(preferences, (draft: Preferences = {}) => {
    const overrideMainInstrument = overrideInstrument && urlInstrumentType ? { type: INSTRUMENT_TYPES[urlInstrumentType], id: overrideInstrument.id } : null
    if (overrideMainInstrument && instrumentValidator(overrideMainInstrument)) {
      draft.instrument = overrideMainInstrument
      isCorrected = true
    } else if (draft.instrument && !instrumentValidator(draft.instrument)) {
      delete draft.instrument
      isCorrected = true
    }
    if (draft.secondInstrument && !instrumentValidator(draft.secondInstrument)) {
      delete draft.secondInstrument
      isCorrected = true
    }
    if (draft.comparisonInstrument && isComparisonInstrumentInvalid(draft.comparisonInstrument as SelectedInstrument, instrumentValidator)) {
      delete draft.comparisonInstrument
      delete draft.selectedMode
      isCorrected = true
    }
    if (draft.base && !instrumentValidator(draft.base)) {
      delete draft.base
      isCorrected = true
    }
  })

  return {
    isCorrected,
    preferences: validatedPreferences,
  }
}

export function useDashboardPreferencesValidation(modelId: string | null) {
  const [activeInstruments, activeInstrumentsStatus] = useActiveInstruments(modelId, true)
  const [preferenceValidationOptions, setPreferenceValidationOptions] = useState<PreferencesValidationOptions | null>(null)
  const { instrument: urlInstrument, clearInstrumentFromUrl } = useGetUrlInstrumentParams()

  const validationOverrides = useMemo(() => ({ urlInstrument }), [urlInstrument])
  const lastValidatedResultRef = useRef<unknown | null>(null)

  useEffect(() => {
    if (urlInstrument?.urlInstrumentType && urlInstrument?.urlInstrumentId) {
      clearInstrumentFromUrl()
    }
  }, [urlInstrument, clearInstrumentFromUrl])

  useEffect(() => {
    // Without a model, instrument validation is impossible. Fire the callback
    // immediately so the dashboard preferences loading gate is not blocked while
    // model preferences are still in-flight. Validation will re-run once modelId
    // arrives and instruments finish loading.
    const isReady = !modelId || activeInstrumentsStatus === OK || activeInstrumentsStatus === ERROR
    if (!preferenceValidationOptions || !isReady) {
      return
    }

    const { preferences, callback } = preferenceValidationOptions

    const preferencesForDashboard = typeof preferences.modelId === "string"
      ? omit(preferences, "modelId")
      : preferences

    let validationResult = {
      isCorrected: false,
      preferences: preferencesForDashboard,
    }

    if (modelId && activeInstrumentsStatus === OK) {
      validationResult = validatePreferences(preferencesForDashboard, activeInstruments, validationOverrides)
    }

    if (!isEqual(validationResult, lastValidatedResultRef.current)) {
      callback(validationResult)
    }
    lastValidatedResultRef.current = validationResult
  }, [activeInstrumentsStatus, preferenceValidationOptions, activeInstruments, validationOverrides, modelId])

  return setPreferenceValidationOptions
}
